import { Component, Input, OnInit, OnDestroy, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ReactiveFormsModule, FormGroup, Validators, FormArray, FormControl } from '@angular/forms';
import { CIPXQuoteRequestIntakeForm, CIPXQuoteRequestIntakeFormCommunicationsContact, businessTypes } from '../../intake-form.types';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { BehaviorSubject, Observable, Subject, combineLatest, map, startWith, take, takeUntil } from 'rxjs';
import { GenericType } from '@sbd-doug-frontend/shared/lib/ui/quick-search/quick-search.types';
import { CIPXQuoteRequestIntakeFormService } from '../../intake-form.service';
import { CURRENT_USER_TOKEN, SharedUserService } from '@sbd-doug-frontend/shared/lib/types/current-user';
import { stages, updateStageConfig } from '../intake-form-bond-lookup/stage-config';

@Component({
  selector: 'cipx-quote-request-intake-form-principal-selection',
  template: `
    <div class="cipx-form-section">
      <fieldset>
        <legend class="cipx-form-section-title">Who's this bond for?</legend>

        <div class="cipx-form-form-group">
          <mat-radio-group
            aria-label="Select Principal Type"
            class="cipx-form-mat-radio-group-rounded"
            [formControl]="quoteRequestForm.controls.principalSelection.controls.principalSelectionType">
            @if (canUseMyself) {
              <mat-radio-button
                value="myself"
                class="cipx-full-width-radio"
                [ngClass]="{
                  'cipx-full-width-radio-selected': quoteRequestForm.controls.principalSelection.controls.principalSelectionType.value === 'myself'
                }">
                <div class="cipx-full-width-radio-label">
                  <span>For Myself</span>
                </div>
              </mat-radio-button>
            }
            <mat-radio-button
              value="new"
              class="cipx-full-width-radio"
              [ngClass]="{
                'cipx-full-width-radio-selected': quoteRequestForm.controls.principalSelection.controls.principalSelectionType.value === 'new'
              }">
              <div class="cipx-full-width-radio-label">
                <span>New Principal</span>
              </div>
            </mat-radio-button>
            <mat-radio-button
              value="existing"
              class="cipx-full-width-radio"
              [ngClass]="{
                'cipx-full-width-radio-selected': quoteRequestForm.controls.principalSelection.controls.principalSelectionType.value === 'existing'
              }">
              <div class="cipx-full-width-radio-label">
                <span>Existing Principal</span>
              </div>
            </mat-radio-button>
          </mat-radio-group>
        </div>

        @if (quoteRequestForm.controls.principalSelection.controls.principalSelectionType.value === 'existing') {
          <div class="cipx-form-form-group">
            <mat-form-field appearance="fill" hideRequiredMarker="true" subscriptSizing="dynamic">
              <mat-label>Select existing principal</mat-label>
              <input
                type="text"
                matInput
                [matAutocomplete]="existingPrincipalAuto"
                [formControl]="quoteRequestForm.controls.principalSelection.controls.existingPrincipal"
                #existingPrincipalTrigger="matAutocompleteTrigger"
                (focus)="existingPrincipalTrigger.openPanel()"
                (click)="existingPrincipalTrigger.openPanel()" />

              <mat-autocomplete
                #existingPrincipalAuto="matAutocomplete"
                (optionSelected)="onExistingPrincipalSelected($event)"
                [displayWith]="existingPrincipalDisplayFn">
                <ng-container *ngIf="filteredPrincipals$ | async as list">
                  <mat-option *ngFor="let principal of list" [value]="principal">
                    {{ principal.label }} <span *ngIf="isPrincipalMeOption(principal)"> (Me)</span>
                  </mat-option>
                  <mat-option *ngIf="list.length === 0" disabled>No principals found</mat-option>
                </ng-container>
              </mat-autocomplete>

              <mat-error *ngIf="quoteRequestForm.controls.principalSelection.controls.existingPrincipal.hasError('required')">
                Please select an existing principal
              </mat-error>
            </mat-form-field>
          </div>
          @if (principalDetailLoading()) {
            <div class="cipx-form-form-group">
              <div class="text-sm text-gray-600 dark:text-white">Loading principal…</div>
            </div>
          } @else if (principalDetail()) {
            <div class="cipx-form-form-group">
              <div class="cipx-full-width-radio cipx-full-width-radio-selected">
                <div class="cipx-full-width-radio-label w-full">
                  <div class="flex flex-col w-full">
                    <div class="font-semibold">
                      {{ principalDetail()?.company_name }}<span *ngIf="isPrincipalMeDetail()"> (Me)</span>
                      <span *ngIf="principalDetail()?.company_dba"> dba {{ principalDetail()?.company_dba }}</span>
                    </div>
                    <div class="mt-1 text-sm text-gray-700 dark:text-black">
                      {{ principalDetail()?.company_address_street_1 }}
                      <span *ngIf="principalDetail()?.company_address_street_2">, {{ principalDetail()?.company_address_street_2 }}</span>
                    </div>
                    <div class="text-sm text-gray-700 dark:text-black">
                      {{ principalDetail()?.company_address_city }}, {{ principalDetail()?.company_address_state_abbreviation }} {{ principalDetail()?.company_address_zip }}
                    </div>
                    <div class="mt-3 text-sm font-medium">Indemnitors</div>
                    <div class="mt-1" *ngIf="principalDetail()?.owners?.length">
                      <ul class="list-none pl-0 m-0">
                        @for (owner of principalDetail()?.owners; track $index) {
                          <li class="py-0.5 dark:text-black">{{ owner.first_name }} {{ owner.last_name }}</li>
                        }
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        }
        @if (quoteRequestForm.controls.principalSelection.controls.principalSelectionType.value === 'myself' && principalDetail() && !principalDetailLoading()) {
          <div class="cipx-form-form-group">
            <div class="cipx-full-width-radio cipx-full-width-radio-selected">
              <div class="cipx-full-width-radio-label w-full">
                  <div class="flex flex-col w-full">
                  <div class="font-semibold">
                    {{ principalDetail()?.company_name }}<span *ngIf="isPrincipalMeDetail()"> (Me)</span>
                    <span *ngIf="principalDetail()?.company_dba"> dba {{ principalDetail()?.company_dba }}</span>
                  </div>
                  <div class="mt-1 text-sm text-gray-700 dark:text-black">
                    {{ principalDetail()?.company_address_street_1 }}
                    <span *ngIf="principalDetail()?.company_address_street_2">, {{ principalDetail()?.company_address_street_2 }}</span>
                  </div>
                  <div class="text-sm text-gray-700 dark:text-black">
                    {{ principalDetail()?.company_address_city }}, {{ principalDetail()?.company_address_state_abbreviation }} {{ principalDetail()?.company_address_zip }}
                  </div>
                  <div class="mt-3 text-sm font-medium">Indemnitors</div>
                  <div class="mt-1" *ngIf="principalDetail()?.owners?.length">
                    <ul class="list-none pl-0 m-0">
                      @for (owner of principalDetail()?.owners; track $index) {
                        <li class="py-0.5 dark:text-black">{{ owner.first_name }} {{ owner.last_name }}</li>
                      }
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
        @if (showMyselfNoPrincipalsMessage && quoteRequestForm.controls.principalSelection.controls.principalSelectionType.value === 'myself') {
          <div class="cipx-form-form-group">
            <div class="cipx-info-message">
              Looks like you haven't requested a quote before. We'll save your information for reuse after you request a quote for the first time.
            </div>
          </div>
        }
      </fieldset>
    </div>
  `,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatRadioModule,
    MatAutocompleteModule,
  ],
})
export class CIPXQuoteRequestIntakeFormPrincipalSelectionComponent implements OnInit, OnDestroy {
  @Input() quoteRequestForm!: FormGroup<CIPXQuoteRequestIntakeForm>;
  @ViewChild('existingPrincipalTrigger', { read: MatAutocompleteTrigger }) existingPrincipalTrigger!: MatAutocompleteTrigger;

  principals: GenericType[] = [];
  filteredPrincipals$!: Observable<GenericType[]>;
  private _intakeFormService = inject(CIPXQuoteRequestIntakeFormService);
  private _cdr = inject(ChangeDetectorRef);
  private _principals$ = new BehaviorSubject<GenericType[]>([]);
  principalDetail = this._intakeFormService.selectedPrincipalDetail;
  principalDetailLoading = this._intakeFormService.principalDetailLoading;
  private _currentUser = inject<SharedUserService | null>(CURRENT_USER_TOKEN, { optional: true }) as SharedUserService | null;
  canUseMyself = !!this._currentUser;
  showMyselfNoPrincipalsMessage = false;
  private _isResetting = false;
  private _selectionInterrupt$ = new Subject<void>();
  private _destroy$ = new Subject<void>();
  private _currentPrincipalUserId(): string | number | null {
    const cu = this._currentUser?.current?.();
    return (cu?.principal_user_id ?? null) as string | number | null;
  }

  isPrincipalMeOption(option: GenericType): boolean {
    const myId = this._currentPrincipalUserId();
    if (!myId) return false;
    const userId = this._intakeFormService.getUserIdForAccessRight(option?.value);
    return String(userId ?? '') === String(myId);
  }

  isPrincipalMeDetail(): boolean {
    const myId = this._currentPrincipalUserId();
    if (!myId) return false;
    const detail: any = this.principalDetail();
    const userId = detail?.user_id ?? null;
    return String(userId ?? '') === String(myId);
  }

  getCompanyTypeLabel(): string {
    const idStr = this.principalDetail()?.company_type_id ?? null;
    const id = idStr !== null ? Number(idStr) : null;
    if (id === null || isNaN(id)) {
      return '';
    }
    const match = businessTypes().find((t) => t.value === id);
    return match ? match.label : '';
  }

  ngOnInit(): void {
    const principalSelectionGroup = this.quoteRequestForm.controls.principalSelection;
    const selectionTypeControl = principalSelectionGroup.controls.principalSelectionType;
    const existingPrincipalControl = principalSelectionGroup.controls.existingPrincipal;

    // Conditional required: existingPrincipal is required when selection type is 'existing'
    selectionTypeControl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      // Interrupt any in-flight async work tied to previous selection, then start a fresh scope
      this._selectionInterrupt$.next();
      this._selectionInterrupt$.complete();
      this._selectionInterrupt$ = new Subject<void>();
      // Skip processing if we're in the middle of a reset
      if (this._isResetting) {
        return;
      }

      if (value === 'myself' && this._currentUser) {
        this._currentUser
          .get()
          .pipe(takeUntil(this._selectionInterrupt$), take(1))
          .subscribe((u) => {
          // user_id for submission
          this.quoteRequestForm.controls.user_id.setValue(u?.principal_user_id ?? null);

          // Individual with 1 owner
          this.quoteRequestForm.controls.bondLookup.controls.company_type_id.setValue(3);
          this._intakeFormService.companyTypeIdChanged.emit(3);
          this._intakeFormService.companyNumberOwnersChanged.emit(1);

          // Prefill principal and owner 0
          const principal = this.quoteRequestForm.controls.requestDetails.controls.principal;
          principal.patchValue({ company_name: u?.name ?? '' });
          const owners = this.quoteRequestForm.controls.requestDetails.controls.owners;
          setTimeout(() => {
            if (owners.length > 0) {
              owners.at(0).patchValue({
                owner_first_name: (u?.firstName ?? u?.name?.split(' ')[0]) || '',
                owner_last_name: (u?.lastName ?? u?.name?.split(' ').slice(1).join(' ')) || '',
                owner_email: u?.email ?? '',
                owner_phone: u?.phone ?? '',
              });
            }
          }, 0);
        });
      } else if (value === 'existing') {
        existingPrincipalControl.setValidators([Validators.required]);
        setTimeout(() => this.existingPrincipalTrigger?.openPanel(), 0);
      } else {
        existingPrincipalControl.clearValidators();
        existingPrincipalControl.setValue('');

        // Clear selected principal detail and communications
        this._intakeFormService.selectedPrincipalDetail.set(null);
        this._intakeFormService.clearStoredCommunicationData();

        // Mark that we're resetting to prevent recursive valueChanges
        this._isResetting = true;

        // Schedule full reset asynchronously to avoid interfering with current change detection
        setTimeout(() => {
          // Interrupt any in-flight async work tied to previous selection, then start a fresh scope
          this._selectionInterrupt$.next();
          this._selectionInterrupt$.complete();
          this._selectionInterrupt$ = new Subject<void>();
          this._intakeFormService.resetForm.emit();
          // After reset completes, set back to 'new' and ensure company type is 3
          setTimeout(() => {
            this.quoteRequestForm.controls.principalSelection.controls.principalSelectionType.setValue('new' as any, { emitEvent: false });
            // Explicitly set company type to Individual/Sole Proprietorship for new principal
            this.quoteRequestForm.controls.bondLookup.controls.company_type_id.setValue(3);
            this._intakeFormService.companyTypeIdChanged.emit(3);
            this._isResetting = false;
          }, 100);
        }, 0);
      }
      existingPrincipalControl.updateValueAndValidity();
    });

    // If default selection is 'myself', set user_id immediately from current user
    if (selectionTypeControl.value === 'myself' && this._currentUser) {
      const current = this._currentUser.current?.() ?? null;
      if (current && current.principal_user_id) {
        this.quoteRequestForm.controls.user_id.setValue(current.principal_user_id);
      } else {
        this._currentUser.get().pipe(take(1)).subscribe((u) => {
          this.quoteRequestForm.controls.user_id.setValue(u?.principal_user_id ?? null);
        });
      }
    }

    // Initialize filtered stream for principals autocomplete (reacts to both input and fetched list)
    this.filteredPrincipals$ = combineLatest([
      existingPrincipalControl.valueChanges.pipe(startWith(existingPrincipalControl.value || '')),
      this._principals$,
    ])
      .pipe(
        takeUntil(this._destroy$),
      map(([value, list]) => this.filterPrincipalsWithList(value, list))
      );

    // Load principals based on selection type
    const loadPrincipalsForSelection = (selection: 'new' | 'existing' | 'myself' | null) => {
      // Interrupt any in-flight async operations when loading for a new selection, then start a fresh scope
      // IMPORTANT: Do NOT interrupt when selection === 'myself' because we may have just started the user-info request
      if (selection !== 'myself') {
        this._selectionInterrupt$.next();
        this._selectionInterrupt$.complete();
        this._selectionInterrupt$ = new Subject<void>();
      }
      // Skip loading if we're in the middle of a reset
      if (this._isResetting) {
        return;
      }

      if (selection === 'myself') {
        this._intakeFormService
          .getPrincipals({ forMyself: true })
          .pipe(takeUntil(this._selectionInterrupt$))
          .subscribe((items) => {
          // If no previous principals, show message and keep form prefilled by current user
          this.principals = items;
          this._principals$.next(items);
          this.showMyselfNoPrincipalsMessage = !items || items.length === 0;
          this._cdr.markForCheck();
          if (this.showMyselfNoPrincipalsMessage) {
            // Nothing to select; detail remains empty; template will render info message
            return;
          }
          // Take first principal and prefill like existing
          const first = items[0];
          // console.log('first', first);
          if (first && first.value) {
            this._intakeFormService
              .getPrincipalDetail(first.value)
              .pipe(takeUntil(this._selectionInterrupt$))
              .subscribe((detail) => {
              // Persist user_id from mapping if present
              const userId = this._intakeFormService.getUserIdForAccessRight(first.value);
              this.quoteRequestForm.controls.user_id.setValue(userId);
              this._intakeFormService.applyPrincipalDetailToForm(detail as any, this.quoteRequestForm);
              // Store communication data for Notifications step to consume later
              this._intakeFormService.storeCommunicationData(
                (detail as any).communication?.communication_info,
                (detail as any).principal_contacts
              );

              this._cdr.markForCheck();
            });
          }
        });
      } else if (selection === 'existing') {
        this._intakeFormService
          .getPrincipals()
          .pipe(takeUntil(this._selectionInterrupt$))
          .subscribe((items) => {
          this.principals = items;
          this._principals$.next(items);
          this.showMyselfNoPrincipalsMessage = false;
          this._cdr.markForCheck();
          setTimeout(() => this.existingPrincipalTrigger?.openPanel(), 0);
        });
      } else {
        // 'new' selection doesn't need principals
        this.principals = [];
        this._principals$.next([]);
        this.showMyselfNoPrincipalsMessage = false;
        this._cdr.markForCheck();

        // Ensure bond lookup stages are reset to defaults on initial/new selection
        stages.forEach((stageConfig) => {
          updateStageConfig(stageConfig, stageConfig.defaultActive, true);
        });
      }
    };

    // Initial load based on default selection
    loadPrincipalsForSelection(selectionTypeControl.value as any);

    // Reload when selection changes
    selectionTypeControl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((val) => loadPrincipalsForSelection(val as any));
  }

  onExistingPrincipalSelected(event: MatAutocompleteSelectedEvent) {
    const selected = event.option.value as GenericType;
    if (!selected || !selected.value) {
      return;
    }
    this._intakeFormService
      .getPrincipalDetail(selected.value)
      .pipe(takeUntil(this._selectionInterrupt$))
      .subscribe((detail) => {
      // Persist user_id alongside selection using principals list mapping
      const userId = this._intakeFormService.getUserIdForAccessRight(selected.value);
      this.quoteRequestForm.controls.user_id.setValue(userId);
      const requestDetailsGroup = this.quoteRequestForm.controls.requestDetails;
      // Prefill principal fields
      requestDetailsGroup.controls.principal.patchValue({
        company_name: detail.company_name ?? '',
        company_dba: detail.company_dba ?? '',
        company_address_street_1: (detail as any).company_address_street_1 ?? '',
        company_address_street_2: (detail as any).company_address_street_2 ?? '',
        company_address_city: (detail as any).company_address_city ?? '',
        company_address_state_abbreviation: (detail as any).company_address_state_abbreviation ?? '',
        company_address_zip: (detail as any).company_address_zip ?? '',
        company_address_county: (detail as any).company_address_county ?? '',
      });

      // Toggle addCompanyAddress if company address fields are present
      const hasCompanyAddress = Boolean(
        (detail as any).company_address_id ||
        (detail as any).company_address_street_1 ||
        (detail as any).company_address_city ||
        (detail as any).company_address_state_abbreviation ||
        (detail as any).company_address_zip
      );
      if (hasCompanyAddress) {
        requestDetailsGroup.controls.addCompanyAddress.setValue(true);
      }

      // Persist user_id for request submission (from principals list we only had access_right_id, detail response should contain user context; if not, fallback from selected option won't have it)
      this.quoteRequestForm.controls.user_id.setValue((this.principalDetail()?.company_type_id, (this.principalDetail() as any)?.user_id ?? null));

      // Company type id (lives in bondLookup)
      const companyTypeId = Number(detail.company_type_id ?? 3);
      this.quoteRequestForm.controls.bondLookup.controls.company_type_id.setValue(companyTypeId);
      this._intakeFormService.companyTypeIdChanged.emit(companyTypeId);

      // Owners mapping
      const owners = Array.isArray(detail.owners) ? detail.owners : [];
      const ownersCount = owners.length > 0 ? owners.length : 1;
      requestDetailsGroup.controls.company_years_in_business.setValue(Number((detail as any).company_years_in_business ?? 0));
      // Let the IntakeForm add/remove controls
      this._intakeFormService.companyNumberOwnersChanged.emit(ownersCount);

      // Store communication data for Notifications step to consume later
      this._intakeFormService.storeCommunicationData(
        (detail as any).communication?.communication_info,
        (detail as any).principal_contacts
      );

      // Patch owners after array is resized by parent
      setTimeout(() => {
        const ownersArray = requestDetailsGroup.controls.owners;
        if (ownersArray.length !== ownersCount) {
          setTimeout(() => this.applyOwners(owners), 0);
          return;
        }
        this.applyOwners(owners);
      }, 0);
    });
  }

  private applyOwners(owners: any[]) {
    const ownersArray = this.quoteRequestForm.controls.requestDetails.controls.owners;
    for (let i = 0; i < ownersArray.length && i < owners.length; i++) {
      const src = owners[i];
      const dst = ownersArray.at(i);
      dst.patchValue({
        owner_first_name: src.first_name ?? '',
        owner_middle_name: src.middle_name ?? '',
        owner_last_name: src.last_name ?? '',
        owner_email: src.email ?? '',
        owner_phone: src.phone ?? '',
        owner_ssn: src.ssn ?? '',
        owner_marital_status: src.marital_status_id ?? null,
        owner_dob: src.dob ?? '',
        owner_years_experience: src.years_experience ? Number(src.years_experience) : null,
        owner_percent_owned: src.percent_owned ? Number(src.percent_owned) : null,
        owner_title: '',
        owner_address_street_1: src.home_address_street_1 ?? '',
        owner_address_street_2: src.home_address_street_2 ?? '',
        owner_address_city: src.home_address_city ?? '',
        owner_address_state_abbreviation: src.home_address_state_abbreviation ?? '',
        owner_address_zip: src.home_address_zip ?? '',
      });
    }
  }

  existingPrincipalDisplayFn(input: any): string {
    if (!input) {
      return '';
    }
    return typeof input === 'string' ? input : (input.label ?? '');
  }

  private filterPrincipals(value: null | string | GenericType): GenericType[] {
    if (!this.principals || this.principals.length === 0) {
      return [];
    }
    if (value === null) {
      return this.principals;
    }
    if (typeof value === 'string') {
      const filterValue = value.toLowerCase();
      if (!filterValue) {
        return this.principals;
      }
      return this.principals.filter(p => p.label.toLowerCase().includes(filterValue));
    }
    return this.principals.filter(p => p === value);
  }

  private filterPrincipalsWithList(value: null | string | GenericType, list: GenericType[]): GenericType[] {
    if (!list || list.length === 0) {
      return [];
    }
    if (value === null) {
      return list;
    }
    if (typeof value === 'string') {
      const filterValue = value.toLowerCase();
      if (!filterValue) {
        return list;
      }
      return list.filter(p => p.label.toLowerCase().includes(filterValue));
    }
    return list.filter(p => p === value);
  }

  private clearAllCommunicationsConfiguration(): void {
    const communications = this.quoteRequestForm.controls.communications as unknown as FormGroup;
    const contactsArray = (communications.get('contacts') as FormArray<FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>> | null) ?? null;
    if (contactsArray) {
      // Reset to a single account_user contact with cleared values and all toggles disabled
      while (contactsArray.length > 0) {
        contactsArray.removeAt(0);
      }
    }

    console.log('🧹 Cleared all communications configuration for new principal');
  }

  private buildCommunicationsForNewPrincipal(): void {
    const communications = this.quoteRequestForm.controls.communications as unknown as FormGroup;
    const contactsArray = (communications.get('contacts') as FormArray<FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>> | null);
    if (!contactsArray) return;

    // Preserve or create account_user at index 0
    let preserved = contactsArray.at(0);
    if (!preserved || (preserved.get('type')?.value !== 'account_user')) {
      preserved = new FormGroup({
        type: new FormControl<'account_user' | 'user' | 'listener'>('account_user', { nonNullable: true }),
        first_name: new FormControl(''),
        last_name: new FormControl(''),
        user_id: new FormControl<number | null>(null),
        email: new FormControl(''),
        phone: new FormControl(''),
        sms_phone: new FormControl(''),
        communication_config: new FormGroup({
          communication_group: new FormGroup({
            quote_announcements: new FormGroup({
              email: new FormGroup({ enabled: new FormControl(false) }),
              phone: new FormGroup({ enabled: new FormControl(false) }),
              sms: new FormGroup({ enabled: new FormControl(false) }),
            }),
            quote_next_renewal: new FormGroup({
              email: new FormGroup({ enabled: new FormControl(false) }),
              phone: new FormGroup({ enabled: new FormControl(false) }),
              sms: new FormGroup({ enabled: new FormControl(false) }),
            }),
          })
        })
      } as any);
    }
    while (contactsArray.length > 0) {
      contactsArray.removeAt(contactsArray.length - 1);
    }
    if (preserved) {
      contactsArray.push(preserved);
    }

    // Prefill account_user name/email/phone from current user if available
    const au = preserved as FormGroup;
    const emailCtrl = au.get('email') as FormControl;
    const phoneCtrl = au.get('phone') as FormControl;
    const smsCtrl = au.get('sms_phone') as FormControl;
    const cu = this._currentUser?.current?.();
    if (cu) {
      (au.get('first_name') as FormControl).setValue(cu.firstName ?? '');
      (au.get('last_name') as FormControl).setValue(cu.lastName ?? '');
      emailCtrl.setValue(cu.email ?? '');
      phoneCtrl.setValue(cu.phone ?? '');
      smsCtrl.setValue(cu.phone ?? '');
    }

    // At principal selection step we don't know owners yet; defer owner/listener contacts to notifications step
  }

  ngOnDestroy(): void {
    // Interrupt any in-flight selection-related work and clean up subscriptions
    this._selectionInterrupt$.next();
    this._selectionInterrupt$.complete();
    this._destroy$.next();
    this._destroy$.complete();
  }
}
